/**
 * MarketFit Service
 * 
 * Service for MarketFit analysis functionality using existing backend endpoints
 * as workarounds until dedicated MarketFit API endpoints are available.
 * 
 * Workarounds implemented:
 * - Deal Management: Uses artifact CRUD endpoints
 * - Calculations: Uses global-properties alignment endpoints with format conversion
 * - Auto-Detection: Handled in frontend (MarketFitContext)
 * 
 * @see docs/api/marketfit_api_gap_analysis.md for workaround details
 */

import { apiClient } from '../apiClient';
import { EnhancedArtifact } from '../../types/artifacts';
import {
  Deal,
  ProductSolution,
  CustomerSituation,
  CustomerActor,
  ConnectionLinksData,
  SolutionInput,
  SolutionBenefit,
  SituationResource,
  SituationNeed,
  DealOperationalState,
} from '../../types/marketFit';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Deal artifact metadata structure (stored in artifact.metadata)
 */
export interface DealArtifactMetadata {
  product_solution_id: string;
  customer_situation_id: string;
  customer_actor_id: string;
  actor_situation_alignment: number; // 0-1 range
  connection_links: ConnectionLinksData;
  deal_score?: number;
  product_situation_alignment?: number;
  product_situation_value?: number;
}

/**
 * Request to create a new deal via artifact endpoint
 */
export interface CreateDealRequest {
  name: string;
  description?: string;
  productSolutionId: string;
  customerSituationId: string;
  customerActorId: string;
  companyId: string;
}

/**
 * Request to update a deal via artifact endpoint
 */
export interface UpdateDealRequest {
  name?: string;
  description?: string;
  state?: DealOperationalState;
  connectionLinks?: ConnectionLinksData;
  actorSituationAlignment?: number;
  scores?: {
    dealScore?: number;
    productSituationAlignment?: number;
    productSituationValue?: number;
  };
}

/**
 * Full deal response with all related artifacts
 */
export interface DealWithRelatedArtifacts {
  deal: Deal;
  solution: ProductSolution | null;
  situation: CustomerSituation | null;
  actor: CustomerActor | null;
}

/**
 * Backend alignment calculation response
 */
export interface BackendAlignmentResponse {
  alignment_percentage: number; // 0-100 range from backend
  breakdown?: {
    input_alignment?: number;
    benefit_alignment?: number;
  };
}

/**
 * Backend deal score response
 */
export interface BackendDealScoreResponse {
  deal_score: number;
}

// ============================================================================
// FORMAT CONVERSION HELPERS
// ============================================================================

/**
 * Convert alignment from frontend format (0-1) to backend format (0-100)
 */
export const alignmentToBackend = (value: number): number => {
  return Math.round(value * 100);
};

/**
 * Convert alignment from backend format (0-100) to frontend format (0-1)
 */
export const alignmentToFrontend = (value: number): number => {
  return value / 100;
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Extract solution data from artifact and its items
 */
const extractSolutionFromArtifact = async (artifact: EnhancedArtifact): Promise<ProductSolution | null> => {
  if (!artifact) return null;

  try {
    // Fetch artifact items (inputs and benefits)
    const itemsResponse = await apiClient.get(`/artifacts/${artifact.id}/items`);
    const items = itemsResponse.data?.items || itemsResponse.data || [];

    const inputs: SolutionInput[] = [];
    const benefits: SolutionBenefit[] = [];

    for (const item of items) {
      const itemType = item.item_type || item.artifact_type;
      const metadata = item.item_metadata || item.metadata || {};

      if (itemType === 'SolutionInput') {
        inputs.push({
          id: item.id,
          name: item.name || metadata.name,
          description: metadata.description,
          isRequired: metadata.is_required ?? true,
          category: metadata.category,
        });
      } else if (itemType === 'SolutionBenefit') {
        benefits.push({
          id: item.id,
          name: item.name || metadata.name,
          description: metadata.description,
          valueProposition: metadata.value_proposition,
          category: metadata.category,
        });
      }
    }

    const metadata = artifact.artifact_metadata || (artifact as any).metadata || {};
    
    return {
      id: artifact.id,
      name: artifact.name,
      description: artifact.description,
      inputs,
      benefits,
      productPrice: metadata.product_price || 0,
      productCostCogs: metadata.product_cost_cogs || 0,
      productCostDevelopment: metadata.product_cost_development || 0,
      artifactId: artifact.id,
    };
  } catch (error) {
    console.error('[MarketFitService] Error extracting solution from artifact:', error);
    return null;
  }
};

/**
 * Extract situation data from artifact and its items
 */
const extractSituationFromArtifact = async (artifact: EnhancedArtifact): Promise<CustomerSituation | null> => {
  if (!artifact) return null;

  try {
    // Fetch artifact items
    const itemsResponse = await apiClient.get(`/artifacts/${artifact.id}/items`);
    const items = itemsResponse.data?.items || itemsResponse.data || [];

    const needs: SituationNeed[] = [];
    const resources: SituationResource[] = [];
    const inputs: { id: string; name: string; description?: string; source?: string }[] = [];
    const outputs: { id: string; name: string; description?: string; value?: number }[] = [];
    const demographics: { id: string; name: string; value: string; category?: string }[] = [];

    for (const item of items) {
      const itemType = item.item_type || item.artifact_type;
      const metadata = item.item_metadata || item.metadata || {};

      switch (itemType) {
        case 'SituationNeed':
          needs.push({
            id: item.id,
            name: item.name || metadata.name,
            description: metadata.description,
            priority: metadata.priority || 'medium',
          });
          break;
        case 'SituationResource':
          resources.push({
            id: item.id,
            name: item.name || metadata.name,
            description: metadata.description,
            cost: metadata.cost || 0,
            isExisting: metadata.is_existing ?? true,
            partialMatch: metadata.partial_match,
          });
          break;
        case 'SituationInput':
          inputs.push({
            id: item.id,
            name: item.name || metadata.name,
            description: metadata.description,
            source: metadata.source,
          });
          break;
        case 'SituationOutput':
          outputs.push({
            id: item.id,
            name: item.name || metadata.name,
            description: metadata.description,
            value: metadata.value,
          });
          break;
        case 'CustomerDemographic':
          demographics.push({
            id: item.id,
            name: item.name || metadata.name,
            value: metadata.value || '',
            category: metadata.category,
          });
          break;
      }
    }

    const metadata = artifact.artifact_metadata || (artifact as any).metadata || {};
    
    // Calculate resource cost
    const resourceCost = resources
      .filter(r => r.isExisting)
      .reduce((sum, r) => sum + (r.cost || 0), 0);

    return {
      id: artifact.id,
      name: artifact.name,
      situationName: metadata.situation_name || artifact.name,
      description: artifact.description,
      marketSegment: metadata.market_segment || '',
      customerType: metadata.customer_type || '',
      demographics,
      needs,
      resources,
      inputs,
      outputs,
      valueGenerated: metadata.value_generated || 0,
      resourceCost,
      artifactId: artifact.id,
    };
  } catch (error) {
    console.error('[MarketFitService] Error extracting situation from artifact:', error);
    return null;
  }
};

/**
 * Extract actor data from artifact and its items
 */
const extractActorFromArtifact = async (artifact: EnhancedArtifact): Promise<CustomerActor | null> => {
  if (!artifact) return null;

  try {
    // Fetch artifact items
    const itemsResponse = await apiClient.get(`/artifacts/${artifact.id}/items`);
    const items = itemsResponse.data?.items || itemsResponse.data || [];

    const priorities: { id: string; name: string; description?: string; rank: number; relatedNeedIds?: string[] }[] = [];
    const motivations: { id: string; name: string; description?: string; strength: 'strong' | 'moderate' | 'weak'; relatedNeedIds?: string[] }[] = [];
    const demographics: { id: string; name: string; value: string }[] = [];

    for (const item of items) {
      const itemType = item.item_type || item.artifact_type;
      const metadata = item.item_metadata || item.metadata || {};

      switch (itemType) {
        case 'ActorPriority':
          priorities.push({
            id: item.id,
            name: item.name || metadata.name,
            description: metadata.description,
            rank: metadata.rank || 1,
            relatedNeedIds: metadata.related_need_ids,
          });
          break;
        case 'ActorMotivation':
          motivations.push({
            id: item.id,
            name: item.name || metadata.name,
            description: metadata.description,
            strength: metadata.strength || 'moderate',
            relatedNeedIds: metadata.related_need_ids,
          });
          break;
        case 'ActorDemographic':
          demographics.push({
            id: item.id,
            name: item.name || metadata.name,
            value: metadata.value || '',
          });
          break;
      }
    }

    const artifactMeta = artifact.artifact_metadata || (artifact as any).metadata || {};

    return {
      id: artifact.id,
      actorName: artifactMeta.actor_name || artifact.name,
      actorRole: artifactMeta.actor_role || '',
      description: artifact.description,
      demographics,
      priorities,
      motivations,
      artifactId: artifact.id,
    };
  } catch (error) {
    console.error('[MarketFitService] Error extracting actor from artifact:', error);
    return null;
  }
};

// ============================================================================
// ERROR HANDLING HELPERS
// ============================================================================

/**
 * Check if API is available by making a lightweight call
 */
export const isApiAvailable = async (): Promise<boolean> => {
  try {
    await apiClient.get('/health');
    return true;
  } catch {
    return false;
  }
};

/**
 * Safely execute an API call with fallback
 */
export const withFallback = async <T>(
  apiCall: () => Promise<T>,
  fallback: T,
  errorMessage?: string
): Promise<{ data: T; fromApi: boolean }> => {
  try {
    const data = await apiCall();
    return { data, fromApi: true };
  } catch (error) {
    if (errorMessage) {
      console.warn(`[MarketFitService] ${errorMessage}:`, error);
    }
    return { data: fallback, fromApi: false };
  }
};

// ============================================================================
// MARKETFIT SERVICE CLASS
// ============================================================================

export class MarketFitService {
  // ==========================================================================
  // DEAL MANAGEMENT (using artifact endpoints as workaround)
  // ==========================================================================

  /**
   * Get a deal by ID with all related artifacts
   * 
   * Workaround: Uses artifact endpoints to fetch deal and related artifacts separately
   */
  static async getDeal(dealId: string): Promise<DealWithRelatedArtifacts | null> {
    try {
      console.log('[MarketFitService] Fetching deal:', dealId);

      // Fetch the deal artifact
      const dealResponse = await apiClient.get(`/artifacts/${dealId}`);
      const dealArtifact = dealResponse.data;

      if (!dealArtifact) {
        console.warn('[MarketFitService] Deal not found:', dealId);
        return null;
      }

      const metadata: DealArtifactMetadata = dealArtifact.artifact_metadata || dealArtifact.metadata || {};

      // Fetch related artifacts in parallel
      const [solutionArtifact, situationArtifact, actorArtifact] = await Promise.all([
        metadata.product_solution_id 
          ? apiClient.get(`/artifacts/${metadata.product_solution_id}`).then(r => r.data).catch(() => null)
          : Promise.resolve(null),
        metadata.customer_situation_id
          ? apiClient.get(`/artifacts/${metadata.customer_situation_id}`).then(r => r.data).catch(() => null)
          : Promise.resolve(null),
        metadata.customer_actor_id
          ? apiClient.get(`/artifacts/${metadata.customer_actor_id}`).then(r => r.data).catch(() => null)
          : Promise.resolve(null),
      ]);

      // Extract full data from related artifacts
      const [solution, situation, actor] = await Promise.all([
        solutionArtifact ? extractSolutionFromArtifact(solutionArtifact) : Promise.resolve(null),
        situationArtifact ? extractSituationFromArtifact(situationArtifact) : Promise.resolve(null),
        actorArtifact ? extractActorFromArtifact(actorArtifact) : Promise.resolve(null),
      ]);

      // Construct the deal object
      const deal: Deal = {
        id: dealArtifact.id,
        name: dealArtifact.name,
        description: dealArtifact.description,
        state: (dealArtifact.current_state as DealOperationalState) || 'INITIALIZE',
        productSolutionId: metadata.product_solution_id || '',
        customerSituationId: metadata.customer_situation_id || '',
        customerActorId: metadata.customer_actor_id || '',
        productSolution: solution || undefined,
        customerSituation: situation || undefined,
        customerActor: actor || undefined,
        connectionLinks: metadata.connection_links || {
          solutionInputToSituationResourceLinks: [],
          solutionBenefitToSituationNeedLinks: [],
        },
        scores: {
          dealScore: metadata.deal_score || 0,
          productSituationAlignment: metadata.product_situation_alignment || 0,
          productSituationValue: metadata.product_situation_value || 0,
          productInputAlignment: 0,
          productBenefitAlignment: 0,
          actorSituationAlignment: metadata.actor_situation_alignment || 0,
        },
        createdAt: dealArtifact.created_at,
        updatedAt: dealArtifact.updated_at,
        artifactId: dealArtifact.id,
      };

      console.log('[MarketFitService] Deal loaded successfully:', deal.name);

      return {
        deal,
        solution,
        situation,
        actor,
      };
    } catch (error: any) {
      console.error('[MarketFitService] Error fetching deal:', error);
      throw new Error(error.response?.data?.detail || error.message || 'Failed to fetch deal');
    }
  }

  /**
   * Create a new deal
   * 
   * Workaround: Uses POST /artifacts with Deal artifact_type
   */
  static async createDeal(request: CreateDealRequest): Promise<Deal> {
    try {
      console.log('[MarketFitService] Creating deal:', request.name);

      const metadata: DealArtifactMetadata = {
        product_solution_id: request.productSolutionId,
        customer_situation_id: request.customerSituationId,
        customer_actor_id: request.customerActorId,
        actor_situation_alignment: 0,
        connection_links: {
          solutionInputToSituationResourceLinks: [],
          solutionBenefitToSituationNeedLinks: [],
        },
      };

      const createPayload = {
        name: request.name,
        description: request.description,
        artifact_type: 'Deal',
        company_id: request.companyId,
        artifact_metadata: metadata,
      };

      const response = await apiClient.post('/artifacts', createPayload);
      const createdArtifact = response.data;

      const deal: Deal = {
        id: createdArtifact.id,
        name: createdArtifact.name,
        description: createdArtifact.description,
        state: 'INITIALIZE',
        productSolutionId: request.productSolutionId,
        customerSituationId: request.customerSituationId,
        customerActorId: request.customerActorId,
        connectionLinks: metadata.connection_links,
        scores: {
          dealScore: 0,
          productSituationAlignment: 0,
          productSituationValue: 0,
          productInputAlignment: 0,
          productBenefitAlignment: 0,
          actorSituationAlignment: 0,
        },
        createdAt: createdArtifact.created_at,
        updatedAt: createdArtifact.updated_at,
        artifactId: createdArtifact.id,
      };

      console.log('[MarketFitService] Deal created successfully:', deal.id);
      return deal;
    } catch (error: any) {
      console.error('[MarketFitService] Error creating deal:', error);
      throw new Error(error.response?.data?.detail || error.message || 'Failed to create deal');
    }
  }

  /**
   * Update an existing deal
   * 
   * Workaround: Uses PUT /artifacts/{id} to update metadata
   */
  static async updateDeal(dealId: string, updates: UpdateDealRequest): Promise<Deal> {
    try {
      console.log('[MarketFitService] Updating deal:', dealId);

      // First, fetch current deal to merge metadata
      const currentResponse = await apiClient.get(`/artifacts/${dealId}`);
      const currentArtifact = currentResponse.data;
      const currentMetadata: DealArtifactMetadata = currentArtifact.artifact_metadata || currentArtifact.metadata || {};

      // Merge metadata updates
      const updatedMetadata: DealArtifactMetadata = {
        ...currentMetadata,
        ...(updates.connectionLinks !== undefined && { connection_links: updates.connectionLinks }),
        ...(updates.actorSituationAlignment !== undefined && { actor_situation_alignment: updates.actorSituationAlignment }),
        ...(updates.scores?.dealScore !== undefined && { deal_score: updates.scores.dealScore }),
        ...(updates.scores?.productSituationAlignment !== undefined && { product_situation_alignment: updates.scores.productSituationAlignment }),
        ...(updates.scores?.productSituationValue !== undefined && { product_situation_value: updates.scores.productSituationValue }),
      };

      const updatePayload: Record<string, any> = {
        artifact_metadata: updatedMetadata,
      };

      if (updates.name !== undefined) {
        updatePayload.name = updates.name;
      }
      if (updates.description !== undefined) {
        updatePayload.description = updates.description;
      }

      const response = await apiClient.put(`/artifacts/${dealId}`, updatePayload);
      const updatedArtifact = response.data;

      const deal: Deal = {
        id: updatedArtifact.id,
        name: updatedArtifact.name,
        description: updatedArtifact.description,
        state: (updatedArtifact.current_state as DealOperationalState) || updates.state || 'INITIALIZE',
        productSolutionId: updatedMetadata.product_solution_id,
        customerSituationId: updatedMetadata.customer_situation_id,
        customerActorId: updatedMetadata.customer_actor_id,
        connectionLinks: updatedMetadata.connection_links,
        scores: {
          dealScore: updatedMetadata.deal_score || 0,
          productSituationAlignment: updatedMetadata.product_situation_alignment || 0,
          productSituationValue: updatedMetadata.product_situation_value || 0,
          productInputAlignment: 0,
          productBenefitAlignment: 0,
          actorSituationAlignment: updatedMetadata.actor_situation_alignment || 0,
        },
        createdAt: updatedArtifact.created_at,
        updatedAt: updatedArtifact.updated_at,
        artifactId: updatedArtifact.id,
      };

      console.log('[MarketFitService] Deal updated successfully:', deal.id);
      return deal;
    } catch (error: any) {
      console.error('[MarketFitService] Error updating deal:', error);
      throw new Error(error.response?.data?.detail || error.message || 'Failed to update deal');
    }
  }

  /**
   * Delete a deal
   * 
   * Workaround: Uses DELETE /artifacts/{id}
   */
  static async deleteDeal(dealId: string): Promise<void> {
    try {
      console.log('[MarketFitService] Deleting deal:', dealId);
      await apiClient.delete(`/artifacts/${dealId}`);
      console.log('[MarketFitService] Deal deleted successfully:', dealId);
    } catch (error: any) {
      console.error('[MarketFitService] Error deleting deal:', error);
      throw new Error(error.response?.data?.detail || error.message || 'Failed to delete deal');
    }
  }

  // ==========================================================================
  // CALCULATIONS (using global-properties endpoints with format conversion)
  // ==========================================================================

  /**
   * Calculate deal score via backend
   * 
   * Workaround: Uses existing /alignment/calculate-deal-score endpoint
   * Note: Backend expects 0-100 range, frontend uses 0-1 range
   */
  static async calculateDealScore(
    productSituationAlignment: number,
    productSituationValue: number,
    actorSituationAlignment: number
  ): Promise<number> {
    try {
      console.log('[MarketFitService] Calculating deal score via backend');

      // Convert from 0-1 to 0-100 for backend
      const params = new URLSearchParams();
      params.append('product_situation_alignment', alignmentToBackend(productSituationAlignment).toString());
      params.append('product_situation_value', productSituationValue.toString());
      params.append('actor_situation_alignment', alignmentToBackend(actorSituationAlignment).toString());

      const response = await apiClient.post<BackendDealScoreResponse>(
        `/alignment/calculate-deal-score?${params.toString()}`
      );

      console.log('[MarketFitService] Backend deal score:', response.data.deal_score);
      return response.data.deal_score;
    } catch (error: any) {
      console.warn('[MarketFitService] Backend calculation failed, falling back to client-side:', error.message);
      // Fallback to client-side calculation
      return productSituationAlignment * productSituationValue * actorSituationAlignment;
    }
  }

  /**
   * Calculate product-situation alignment via backend
   * 
   * Workaround: Uses existing /alignment/calculate-product-situation endpoint
   * Note: Different input format - backend expects property dictionaries
   */
  static async calculateProductSituationAlignment(
    solutionInputs: SolutionInput[],
    solutionBenefits: SolutionBenefit[],
    situationResources: SituationResource[],
    situationNeeds: SituationNeed[]
  ): Promise<{ alignment: number; inputAlignment?: number; benefitAlignment?: number }> {
    try {
      console.log('[MarketFitService] Calculating product-situation alignment via backend');

      // Format for existing backend endpoint
      const request = {
        solution_properties: {
          solution_inputs: solutionInputs.map(i => ({
            name: i.name,
            description: i.description,
            is_required: i.isRequired,
          })),
          solution_benefits: solutionBenefits.map(b => ({
            name: b.name,
            description: b.description,
            value_proposition: b.valueProposition,
          })),
        },
        situation_properties: {
          situation_assets: situationResources.map(r => ({
            name: r.name,
            description: r.description,
            cost: r.cost,
            is_existing: r.isExisting,
          })),
          situation_needs: situationNeeds.map(n => ({
            name: n.name,
            description: n.description,
            priority: n.priority,
          })),
        },
      };

      const response = await apiClient.post<BackendAlignmentResponse>(
        '/alignment/calculate-product-situation',
        request
      );

      // Convert from 0-100 to 0-1
      const alignment = alignmentToFrontend(response.data.alignment_percentage);
      
      console.log('[MarketFitService] Backend alignment:', alignment);
      
      return {
        alignment,
        inputAlignment: response.data.breakdown?.input_alignment 
          ? alignmentToFrontend(response.data.breakdown.input_alignment)
          : undefined,
        benefitAlignment: response.data.breakdown?.benefit_alignment
          ? alignmentToFrontend(response.data.breakdown.benefit_alignment)
          : undefined,
      };
    } catch (error: any) {
      console.warn('[MarketFitService] Backend calculation failed:', error.message);
      throw error;
    }
  }

  // ==========================================================================
  // LIST OPERATIONS
  // ==========================================================================

  /**
   * List all deals for a company
   */
  static async listDeals(companyId: string): Promise<Deal[]> {
    try {
      console.log('[MarketFitService] Listing deals for company:', companyId);

      // Use artifact endpoint with type filter
      const params = new URLSearchParams();
      params.append('company_id', companyId);
      params.append('artifact_type', 'Deal');

      const response = await apiClient.get(`/artifacts?${params.toString()}`);
      const artifacts = response.data?.artifacts || response.data || [];

      const deals: Deal[] = artifacts.map((artifact: EnhancedArtifact) => {
        const metadata: DealArtifactMetadata = artifact.artifact_metadata || (artifact as any).metadata || {};
        return {
          id: artifact.id,
          name: artifact.name,
          description: artifact.description,
          state: ((artifact as any).current_state as DealOperationalState) || 'INITIALIZE',
          productSolutionId: metadata.product_solution_id || '',
          customerSituationId: metadata.customer_situation_id || '',
          customerActorId: metadata.customer_actor_id || '',
          connectionLinks: metadata.connection_links || {
            solutionInputToSituationResourceLinks: [],
            solutionBenefitToSituationNeedLinks: [],
          },
          scores: {
            dealScore: metadata.deal_score || 0,
            productSituationAlignment: metadata.product_situation_alignment || 0,
            productSituationValue: metadata.product_situation_value || 0,
            productInputAlignment: 0,
            productBenefitAlignment: 0,
            actorSituationAlignment: metadata.actor_situation_alignment || 0,
          },
          createdAt: artifact.created_at,
          updatedAt: artifact.updated_at,
          artifactId: artifact.id,
        };
      });

      console.log('[MarketFitService] Found deals:', deals.length);
      return deals;
    } catch (error: any) {
      console.error('[MarketFitService] Error listing deals:', error);
      throw new Error(error.response?.data?.detail || error.message || 'Failed to list deals');
    }
  }

  /**
   * List solutions for a company
   */
  static async listSolutions(companyId: string): Promise<ProductSolution[]> {
    try {
      console.log('[MarketFitService] Listing solutions for company:', companyId);

      const params = new URLSearchParams();
      params.append('company_id', companyId);
      params.append('artifact_type', 'Solution');

      const response = await apiClient.get(`/artifacts?${params.toString()}`);
      const artifacts = response.data?.artifacts || response.data || [];

      const solutions: ProductSolution[] = await Promise.all(
        artifacts.map((artifact: EnhancedArtifact) => extractSolutionFromArtifact(artifact))
      );

      return solutions.filter((s): s is ProductSolution => s !== null);
    } catch (error: any) {
      console.error('[MarketFitService] Error listing solutions:', error);
      throw new Error(error.response?.data?.detail || error.message || 'Failed to list solutions');
    }
  }

  /**
   * List situations for a company
   */
  static async listSituations(companyId: string): Promise<CustomerSituation[]> {
    try {
      console.log('[MarketFitService] Listing situations for company:', companyId);

      const params = new URLSearchParams();
      params.append('company_id', companyId);
      params.append('artifact_type', 'Situation');

      const response = await apiClient.get(`/artifacts?${params.toString()}`);
      const artifacts = response.data?.artifacts || response.data || [];

      const situations: CustomerSituation[] = await Promise.all(
        artifacts.map((artifact: EnhancedArtifact) => extractSituationFromArtifact(artifact))
      );

      return situations.filter((s): s is CustomerSituation => s !== null);
    } catch (error: any) {
      console.error('[MarketFitService] Error listing situations:', error);
      throw new Error(error.response?.data?.detail || error.message || 'Failed to list situations');
    }
  }

  /**
   * List actors for a company
   */
  static async listActors(companyId: string): Promise<CustomerActor[]> {
    try {
      console.log('[MarketFitService] Listing actors for company:', companyId);

      const params = new URLSearchParams();
      params.append('company_id', companyId);
      params.append('artifact_type', 'Actor');

      const response = await apiClient.get(`/artifacts?${params.toString()}`);
      const artifacts = response.data?.artifacts || response.data || [];

      const actors: CustomerActor[] = await Promise.all(
        artifacts.map((artifact: EnhancedArtifact) => extractActorFromArtifact(artifact))
      );

      return actors.filter((a): a is CustomerActor => a !== null);
    } catch (error: any) {
      console.error('[MarketFitService] Error listing actors:', error);
      throw new Error(error.response?.data?.detail || error.message || 'Failed to list actors');
    }
  }
}

export default MarketFitService;
